"use client";

import { Compass, Lightbulb, PencilRuler, Sparkles, Zap } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Installation() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const steps = [{
    number: "SCHRITT 01",
    title: "Analyse & Vor-Ort-Termin",
    description: "Wir erfassen Ihre Wohnsituation, die Bausubstanz und Ihre individuellen Anforderungen bei einem persönlichen Beratungstermin.",
    Icon: Compass
  }, {
    number: "SCHRITT 02",
    title: "Planung & Konzept",
    description: "Unsere Experten entwerfen ein maßgeschneidertes Kanalsystem, das perfekt in Ihre Architektur integriert wird.",
    Icon: PencilRuler
  }, {
    number: "SCHRITT 03",
    title: "System-Engineering",
    description: "Wir berechnen die optimale Luftführung und wählen hocheffiziente Komponenten für maximale Energieeinsparung aus.",
    Icon: Lightbulb
  }, {
    number: "SCHRITT 04",
    title: "Präzise Montage",
    description: "Unsere zertifizierten Techniker installieren das System diskret im Dachboden und verlegen die Kanäle fachgerecht - oder Sie machen es ganz einfach selbst.",
    Icon: Zap
  }, {
    number: "SCHRITT 05",
    title: "Inbetriebnahme & Einweisung",
    description: "Wir kalibrieren die Luftströme, testen die Steuerung und übergeben Ihnen ein perfekt temperiertes Zuhause.",
    Icon: Sparkles
  }];
  return <section data-section-id="2524" id="der-weg-zur-installation" className="relative bg-background text-foreground py-32 px-4 overflow-hidden">
      {}
      <div aria-hidden="true" className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{
      backgroundImage: "linear-gradient(to right, var(--foreground) 1px, transparent 1px), linear-gradient(to bottom, var(--foreground) 1px, transparent 1px)",
      backgroundSize: "64px 64px"
    }} />

      <div className="relative max-w-7xl mx-auto">
        {}
        <div ref={ref} className={`text-center mb-12 transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <h2 className="font-default text-4xl md:text-5xl lg:text-6xl font-light tracking-tight">
            Der Weg zur Installation.
          </h2>
          <div className="mt-6 flex justify-center">
            <div className="h-px w-16 bg-border" />
          </div>
        </div>

        {}
        <div className="hidden lg:block relative pt-36 pb-16">
          {}
          <div className="absolute left-0 right-0 top-1/2 h-px bg-border" />

          <div className="relative grid grid-cols-5 gap-4">
            {steps.map((step, i) => {
            const isTop = i % 2 === 0;
            const Icon = step.Icon;
            return <div data-index={i} key={i} className="relative flex flex-col items-center group" style={{
              minHeight: "320px"
            }}>
                  {}
                  {isTop && <div className="absolute bottom-1/2 mb-12 w-full px-2 transition-transform duration-300 group-hover:-translate-y-1">
                      <div className="bg-card border border-border rounded-lg p-6 shadow-md">
                        <Icon className="w-5 h-5 text-primary mb-4" strokeWidth={1.5} />
                        <p className="font-default text-[10px] tracking-[0.25em] text-primary uppercase mb-3">
                          {step.number}
                        </p>
                        <h3 className="font-default text-2xl font-medium leading-tight mb-3 relative inline-block">
                          {step.title}
                          <span className="absolute left-0 -bottom-1 h-px w-0 bg-primary transition-all duration-300 group-hover:w-full" />
                        </h3>
                        <p className="text-sm text-muted-foreground leading-relaxed">
                          {step.description}
                        </p>
                      </div>
                    </div>}

                  {}
                  <div className="absolute top-1/2 -translate-y-1/2 flex items-center justify-center">
                    <div className="relative">
                      <span className="absolute inset-0 rounded-lg bg-primary/30 animate-ping" style={{
                    animationDuration: "4s"
                  }} />
                      <div className="relative w-4 h-4 rounded-lg bg-primary transition-transform duration-300 group-hover:scale-150" />
                    </div>
                  </div>

                  {}
                  {!isTop && <div className="absolute top-1/2 mt-12 w-full px-2 transition-transform duration-300 group-hover:translate-y-1">
                      <div className="bg-card border border-border rounded-lg p-6 shadow-md">
                        <Icon className="w-5 h-5 text-primary mb-4" strokeWidth={1.5} />
                        <p className="font-default text-[10px] tracking-[0.25em] text-primary uppercase mb-3">
                          {step.number}
                        </p>
                        <h3 className="font-default text-2xl font-medium leading-tight mb-3 relative inline-block">
                          {step.title}
                          <span className="absolute left-0 -bottom-1 h-px w-0 bg-primary transition-all duration-300 group-hover:w-full" />
                        </h3>
                        <p className="text-sm text-muted-foreground leading-relaxed">
                          {step.description}
                        </p>
                      </div>
                    </div>}
                </div>;
          })}
          </div>
        </div>

        {}
        <div className="lg:hidden relative mt-36">
          {}
          <div className="absolute left-6 top-2 bottom-2 w-px bg-border" />

          <div className="space-y-10">
            {steps.map((step, i) => {
            const Icon = step.Icon;
            return <div data-index={i} key={i} className="relative pl-16 group">
                  {}
                  <div className="absolute left-6 top-4 -translate-x-1/2 flex items-center justify-center">
                    <span className="absolute inset-0 rounded-lg bg-primary/30 animate-ping" style={{
                  animationDuration: "4s"
                }} />
                    <div className="relative w-4 h-4 rounded-lg bg-primary" />
                  </div>

                  <div className="bg-card border border-border rounded-lg p-6 shadow-md transition-transform duration-300 group-hover:-translate-y-1">
                    <Icon className="w-5 h-5 text-primary mb-3" strokeWidth={1.5} />
                    <p className="font-default text-[10px] tracking-[0.25em] text-primary uppercase mb-2">
                      {step.number}
                    </p>
                    <h3 className="font-default text-xl font-medium leading-tight mb-2 relative inline-block">
                      {step.title}
                      <span className="absolute left-0 -bottom-1 h-px w-0 bg-primary transition-all duration-300 group-hover:w-full" />
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </div>;
          })}
          </div>
        </div>
      </div>
    </section>;
}